import Select, {components} from "react-select";
import React, {useRef, useState} from 'react';
import CreatableSelect from "react-select/creatable";

const MultiSelect = (props) => {
    const [selectInput, setSelectInput] = useState("");
    const isAllSelected = useRef(false);
    const selectAllLabel = useRef("Selecionar todos");
    const allOption = {value: "*", label: selectAllLabel.current};
    const currentInputValue = props.inputValue ?? selectInput;
    const normalizeText = (value) => (value ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

    const comparator = (v1, v2) => v1.label.localeCompare(v2.label);
    const getOptionKey = ({value}) => String(value);
    const filterOptions = (options, input) => {
        const normalizedInput = normalizeText(input);

        return options
            ?.filter(({label}) => normalizeText(label).includes(normalizedInput))
            .sort(comparator);
    };

    let filteredOptions = filterOptions(props.options, currentInputValue);
    let filteredSelectedOptions = filterOptions(props.value, currentInputValue);

    const customStyles = {
        container: (base) => ({
            ...base,
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
        }),
        control: (base) => {
            return {
                ...base,
                height: "32px",
                minHeight: "32px",
                minWidth: 0,
                maxWidth: "100%",
            };
        },
        valueContainer: (base) => ({
            ...base,
            height: "100%",
            minWidth: 0,
            maxWidth: "100%",
            padding: "0 8px",
            alignItems: "center",
            flexWrap: "nowrap",
            overflow: "hidden",
            fontSize: "14px"
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
        option: (styles, {isSelected, isFocused}) => {
            return {
                ...styles,
                backgroundColor:
                    isSelected && !isFocused
                        ? null
                        : isFocused && !isSelected
                            ? styles.backgroundColor
                            : isFocused && isSelected
                                ? "#DEEBFF"
                                : null,
                color: isSelected ? null : null,
                fontSize: "14px"
            };
        },
        menu: (def) => ({...def, zIndex: 2000}),
        menuPortal: (base) => ({...base, zIndex: 3000}),
        indicatorsContainer: (base) => ({
            ...base,
            height: "100%",
            alignItems: "center",
        }),
        clearIndicator: (base) => ({...base, padding: "6px"}),
        dropdownIndicator: (base) => ({...base, padding: "6px"}),
    };

    const selectedOptionKeys = new Set(filteredSelectedOptions.map(getOptionKey));
    isAllSelected.current = filteredOptions.length > 0 && filteredOptions.every((option) => selectedOptionKeys.has(getOptionKey(option)));

    if (filteredSelectedOptions?.length > 0) {
        if (filteredSelectedOptions?.length === filteredOptions?.length)
            selectAllLabel.current = `${filteredOptions.length} opções selecionadas`;
        else
            selectAllLabel.current = `${filteredSelectedOptions?.length} / ${filteredOptions.length} selecionados`;
    } else selectAllLabel.current = "Selecionar todos";

    allOption.label = selectAllLabel.current;

    const Option = (props) => (
        <components.Option {...props}>
            {
                props.value === "*" && !isAllSelected.current && filteredSelectedOptions?.length > 0 ? (
                    <input
                        key={props.value}
                        type="checkbox"
                        ref={(input) => {
                            if (input) input.indeterminate = true;
                        }}
                    />
                ) : (
                    <input
                        key={props.value}
                        type="checkbox"
                        checked={props.isSelected || isAllSelected.current}
                        onChange={() => {}}
                    />
                )
            }
            <label style={{marginLeft: "5px", maxWidth: "90%", height: "18px"}} className={"truncate multi-selected-label"} title={props.label}>
                {props.label}
            </label>
        </components.Option>
    );

    const MultiValue = ({ selectProps, data }) => {
        const index = selectProps.value.findIndex(selected => selected.value === data.value);
        const isLastSelected = index === selectProps.value.length - 1;

        const multiStyle = {
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            wordBreak: 'break-all',
            overflow: 'hidden',
            OTextOverflow: 'ellipsis',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            lineHeight: '20px',
        };

        if (isLastSelected && !selectProps.inputValue) {
            return (
                <div style={multiStyle}>
                    {selectProps.value.map(e => e.label).join(", ")}
                </div>
            );
        }
        return null;
    };

    const customFilterOption = ({value, label}, input) =>
        (value !== "*" && normalizeText(label).includes(normalizeText(input))) ||
        (value === "*" && filteredOptions?.length > 0);

    const onInputChange = (inputValue, event) => {
        if (props.onInputChange) {
            props.onInputChange(inputValue, event);
            return;
        }

        if (event.action === "input-change") setSelectInput(inputValue);
        else if (event.action === "menu-close" && currentInputValue !== "")
            setSelectInput("");
    };

    const onKeyDown = (e) => {
        if ((e.key === " " || e.key === "Enter") && !currentInputValue) e.preventDefault();
    };

    const handleChange = (selected, actionMeta) => {
        if (actionMeta?.option?.value !== allOption.value) {
            return props.onChange(selected ?? []);
        }

        if (isAllSelected.current) {
            const filteredValues = new Set(filteredOptions.map(getOptionKey));

            return props.onChange(
                (props.value ?? []).filter((option) => !filteredValues.has(getOptionKey(option)))
            );
        }

        const selectedValues = new Set((props.value ?? []).map(getOptionKey));

        return props.onChange(
            [
                ...(props.value ?? []),
                ...filteredOptions.filter((option) => !selectedValues.has(getOptionKey(option))),
            ].sort(comparator)
        );
    };

    const SelectType = props.isCreatable ? CreatableSelect : Select;

    return (
        <SelectType
            {...props}
            inputValue={currentInputValue}
            onInputChange={onInputChange}
            onKeyDown={onKeyDown}
            options={[allOption, ...props.options]}
            onChange={handleChange}
            components={{
                Option,
                MultiValue,
                ...props.components,
            }}
            filterOption={customFilterOption}
            menuPlacement={props.menuPlacement ?? "auto"}
            styles={customStyles}
            isMulti
            closeMenuOnSelect={false}
            tabSelectsValue={false}
            backspaceRemovesValue={false}
            hideSelectedOptions={false}
            blurInputOnSelect={false}
            ref={props.selectRef}
            createOptionPosition={props.isCreatable ? "first" : undefined}
        />
    );
};


export default MultiSelect;
